import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  ViewChild,
} from '@angular/core';
import { Upgrade } from '@core/models';
import { CartService, DvmService, SelectedSeats } from '@core/services';
import { animate } from 'animejs';
import { Subscription } from 'rxjs';
import { UpgradesService } from './upgrades.service';

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-upgrades',
  templateUrl: './upgrades.component.html',
  styleUrl: './upgrades.component.css',
})
export class UpgradesComponent implements OnInit, OnDestroy {
  // VIEWCHILD
  @ViewChild('benefitsContainer')
  public benefitsContainer: ElementRef<HTMLDivElement>;

  // SERVICES
  private upgradesService: UpgradesService = inject(UpgradesService);
  private cartService: CartService = inject(CartService);
  private viewer: MapViewerService = inject(DvmService).viewerService;

  // STATE
  protected is3dOpen: Signal<boolean> = inject(DvmService).is3dView;
  protected showBenefits: boolean = false;
  protected showUpgrades: boolean = false;
  protected totalCart: number = 0;

  protected get upgradesVars(): {
    upgrades: Upgrade[];
    reachingUpgrade: Upgrade | null;
    acquiredUpgrade: Upgrade | null;
    availableUpgrades: Upgrade[];
    acquiredUpgrades: Upgrade[];
  } {
    return {
      upgrades: this.upgradesService.upgrades,
      reachingUpgrade: this.upgradesService.reachingUpgrade,
      acquiredUpgrade: this.upgradesService.acquiredUpgrade,
      availableUpgrades: this.upgradesService.availableUpgrades || [],
      acquiredUpgrades: this.upgradesService.acquiredUpgrades || [],
    };
  }

  // SUBSCRIPTIONS
  private subscriptions: Subscription[] = [];

  // TEMPLATE VARS
  public templateVars: { text: string } = {
    text: 'Group Benefits',
  };

  // LIFE CYCLE HOOKS
  public ngOnInit(): void {
    this.initComponent();
  }

  public ngOnDestroy(): void {
    this.destroyComponent();
  }

  // METHODS
  public toggleBenefits(): void {
    this.showBenefits = !this.showBenefits;

    const container: HTMLDivElement = this.benefitsContainer.nativeElement;

    animate(container, {
      scale: this.showBenefits ? 1 : 0,
      ease: 'easeInOutQuad',
      duration: 330,
    });
  }

  /**
   * Se llama en cada carga del mapa, si la propiedad showUpgrades es falsa,
   * se muestra el componente de upgrades de aqui en adelante. Si ya estaba siendo mostrado se deja igual.
   *
   * Si los benefits estan abiertos en la carga del mapa, se cierran.
   */
  private handleLoad(): void {
    // SI NO HAY UPSELLS NO MOSTRAMOS NADA
    if (this.upgradesService.upgrades?.length === 0) {
      return;
    }

    if (!this.showUpgrades) {
      this.showUpgrades = true;
    }

    if (this.showBenefits) {
      this.toggleBenefits();
    }
  }

  private initComponent(): void {
    // Nos suscribimos al viewer para saber cuando sucede una carga en el mapa y lanzamos handleLoad()
    const subscription1 = this.viewer.waitInitialize().subscribe({
      next: () => {
        const load = this.viewer
          .getObservable('load_success')
          .subscribe(() => this.handleLoad());
        this.subscriptions.push(load);
      },
    });
    this.subscriptions.push(subscription1);

    // Nos suscribimos al servicio de carrito para saber cuando se seleccionan asientos y lanzamos checkCartSeatsForUpgradesList()
    const subscription2 = this.cartService.selectedSeats$.subscribe({
      next: (selectedSeats: SelectedSeats) => {
        const totalCartSeats = Object.keys(selectedSeats).length;
        this.upgradesService.checkCartSeatsForUpgradesList(totalCartSeats);
        this.upgradesService.checkTotalSeatsForUpgradesMessages(totalCartSeats);
      },
      error: error => console.error(error),
    });
    this.subscriptions.push(subscription2);

    const subscription3 =
      this.upgradesService.totalCartAndFiltersSeats$.subscribe({
        next: (response: number) => {
          this.totalCart = response;
          this.upgradesService.checkTotalSeatsForUpgradesMessages(
            this.totalCart
          );
        },
      });
    this.subscriptions.push(subscription3);
  }

  private destroyComponent(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
